import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { createCompanyApiKeyQuery, getCompanyApiKeysQuery, getTeamMembersQuery, removeCompanyApiKeyQuery } from "@query";
import { ApiKeysForm } from "@forms";
import {
  Alert,
  Dialog,
  Table,
  TableCell,
  TableRow,
  Button,
  ClearButton,
  TextField,
} from "@components";
import { CircularProgress, Grid } from "@material-ui/core";
import { Add, DeleteOutline } from "@material-ui/icons";

export const ApiKeysAccess = () => {
  const { t } = useTranslation();
  
  const [ keys, setKeys ] = useState([]);
  const [ members, setMembers ] = useState([]);
  const [ isLoading, setLoading ] = useState(true);
  const [ isCreateOpen, setCreateOpen ] = useState(false);
  const [ keyToRemove, setKeyToRemove ] = useState();
  const [ secret, setSecret ] = useState();
  const [ error, setError ] = useState();
  
  const activeMembers = useMemo(() => (
    members.filter(member => Number(member.status) === 1)
  ), [members]);
  
  const loadData = async () => {
    setLoading(true);
    
    try {
      const [apiKeys, teamMembers] = await Promise.all([
        getCompanyApiKeysQuery(),
        getTeamMembersQuery(),
      ]);
      setKeys(apiKeys);
      setMembers(teamMembers);
    } catch (loadError) {
      setError(t("errors.network"));
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadData();
  }, []);
  
  const closeCreateDialog = () => {
    setCreateOpen(false);
  };
  
  const createKey = async (data) => {
    try {
      const createdKey = await createCompanyApiKeyQuery({
        userId: data.userId,
        name: data.name,
        scope: data.scope,
      });
      closeCreateDialog();
      setSecret(createdKey.apiKey);
      await loadData();
    } catch (createError) {
      setError(t("errors.network"));
    }
  };
  
  const removeKey = async () => {
    if (!keyToRemove) return;
    
    try {
      await removeCompanyApiKeyQuery(keyToRemove.id);
      setKeyToRemove(undefined);
      await loadData();
    } catch (removeError) {
      setError(t("errors.network"));
    }
  };
  
  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
    } catch (copyError) {
      setError(t("errors.network"));
    }
  };
  
  return (
    <>
      <Grid
        item container
        justifyContent="space-between"
        alignItems="center"
        className="mb-4"
      >
        <h2>{t("views.settings.menu.api_keys")}</h2>
        <Button
          variant="contained"
          color="primary"
          icon={<Add/>}
          onClick={() => setCreateOpen(true)}
        >
          {t("views.settings.api_keys.create")}
        </Button>
      </Grid>
      
      {isLoading ? (
        <Grid item container justifyContent="center">
          <CircularProgress size={24}/>
        </Grid>
      ) : (
        <Table
          id="apiKeys"
          headers={[
            {label: t("views.settings.api_keys.name"), width: 220},
            {label: t("views.settings.api_keys.user"), width: 220},
            {label: t("views.settings.api_keys.scope"), width: 120},
            {label: t("views.settings.api_keys.key"), width: 160},
            {label: t("views.settings.api_keys.created"), width: 160},
            {width: 48},
          ]}
        >
          {keys.map(key => (
            <TableRow key={key.id}>
              <TableCell width={220}>
                {key.name || t("common.unnamed")}
              </TableCell>
              <TableCell width={220}>{ key.assignedUser }</TableCell>
              <TableCell width={120}>{ key.permissionScope }</TableCell>
              <TableCell width={160}>{ key.apiKeyPreview}</TableCell>
              <TableCell width={160}>{ key.createdAt }</TableCell>
              <TableCell width={48}>
                <Button
                  name={t("common.delete")}
                  color="secondary"
                  icon={<DeleteOutline color="error"/>}
                  onClick={() => setKeyToRemove(key)}
                />
              </TableCell>
            </TableRow>
          ))}
          {!keys.length && (
            <h3 className="px-4 py-6">
              {t("views.settings.api_keys.empty")}
            </h3>
          )}
        </Table>
      )}
      
      <Dialog
        open={isCreateOpen}
        onClose={closeCreateDialog}
        title={t("views.settings.api_keys.create")}
        width={520}
      >
        <ApiKeysForm
          onSubmit={createKey}
          onCancel={closeCreateDialog}
          members={activeMembers}
        />
      </Dialog>
      
      <Alert
        open={Boolean(secret)}
        title={t("views.settings.api_keys.secret_title")}
        width={620}
        cancelText={t("common.close")}
        onCancel={() => setSecret(undefined)}
      >
        <p className="mb-4">
          {t("views.settings.api_keys.secret_warning")}
        </p>
        <Grid
          item
          container
          wrap="nowrap"
          alignItems="center"
          className="mb-4"
        >
          <TextField
            id="shareLink"
            value={secret}
            className="m-0"
            onClick={copySecret}
          />
          <ClearButton className="ml-2" onClick={copySecret}>
            {t("common.copy")}
          </ClearButton>
        </Grid>
      </Alert>
      
      <Alert
        open={Boolean(keyToRemove)}
        title={t("views.settings.api_keys.revoke_title")}
        acceptText={t("views.settings.api_keys.revoke")}
        onAccept={removeKey}
        onCancel={() => setKeyToRemove(undefined)}
      >
        <p>
          {t("views.settings.api_keys.revoke_text", {
            name: keyToRemove?.name || t("common.unnamed"),
          })}
        </p>
      </Alert>
      
      <Alert
        open={Boolean(secret)}
        title={t("views.settings.api_keys.secret_title")}
        width={620}
        cancelText={t("common.close")}
        onCancel={() => setSecret(undefined)}
      >
        <p className="mb-4">{t("views.settings.api_keys.secret_warning")}</p>
        <Grid
          item
          container
          wrap="nowrap"
          alignItems="center"
          className="mb-4"
        >
          <TextField
            id="shareLink"
            value={secret}
            className="m-0"
            onClick={copySecret}
          />
          <ClearButton
            className="ml-2"
            onClick={copySecret}
          >
            {t("common.copy")}
          </ClearButton>
        </Grid>
      </Alert>
      <Alert
        open={Boolean(keyToRemove)}
        title={t("views.settings.api_keys.revoke_title")}
        acceptText={t("views.settings.api_keys.revoke")}
        onAccept={removeKey}
        onCancel={() => setKeyToRemove(undefined)}
      >
        <p>
          {t("views.settings.api_keys.revoke_text", { name: keyToRemove?.name || t("common.unnamed") })}
        </p>
      </Alert>
      
      <Alert
        isOpen={Boolean(error)}
        title={error}
        acceptText={t("common.close")}
        onAccept={() => setError(undefined)}
      />
    </>
  )
}